'use client';

import type {
  CheckoutTireEfficiencyFlag,
  PowerEfficiencyFlag,
} from '@packages/gql/generated/shopping/graphql';
import { isValidColor } from '@packages/shared/src/ui/EnergyEfficiencyFlag/colorsForEnergyEfficiency';
import type { EfficiencyClass } from '../helper/isEfficiencyClass';
import { isEfficiencyClass } from '../helper/isEfficiencyClass';
import { DownloadItem } from '../../DownloadItem/DownloadItem.client';
import { tireOrderToUiTypeMap } from '../helper/mapping';
import { TireEfficiencyFlagItem } from '../atoms/TireEfficiencyFlagItem/TireEfficiencyFlagItem.client';
import { EnergyEfficiencyFlagItem } from '../atoms/EnergyEfficiencyFlagItem/EnergyEfficiencyFlagItem';
import type { EnergieEfficiency, EnergieEfficiencyOld } from '../types';

/**
 * Single power efficiency flag entry — typed as plain props so basket, wishlist
 * and AddToBasketLayer can map their slightly different GraphQL shapes
 * (`BasketPowerEfficiencyFlag`, `WishlistProductEnergyEfficiency`) into the same component.
 */
export type EfficiencyFlagsPowerFlag = {
  /**
   * Energy class label. Either a current EU class letter A-G, or a legacy level
   * such as "A+++" when `hasOldLabel` is set. Items that are neither a valid class
   * nor a renderable legacy label (e.g. legacy backend behaviour where tire data was
   * smuggled into power flags with composite levels like "B | 71 dB") are silently skipped.
   */
  level: string | null;
  /** Localized label, used as overlay dialog title (e.g. "Energieeffizienzklasse"). */
  text: string | null;
  /** Image URL shown in the overlay dialog. */
  link: string;
  /** Optional product data sheet URL, rendered as an inline download link. */
  dataSheet?: string | null;
  /**
   * Whether the product still carries a legacy EEK label (the "A+++ to D" scale).
   * Optional because `WishlistProductEnergyEfficiency` does not expose it — those
   * flags therefore keep rendering on the current A-G scale.
   */
  hasOldLabel?: PowerEfficiencyFlag['hasOldLabel'] | null;
  /**
   * Colour of the legacy label bar. Only meaningful together with `hasOldLabel`, and
   * nullable because the basket and checkout shapes declare it as `Maybe<String>`
   * regardless of `hasOldLabel` — `isLegacyPowerFlag` below establishes the pairing.
   */
  color?: PowerEfficiencyFlag['color'] | null;
};

/**
 * Narrows a flag to the legacy shape that `<EnergyEfficiencyFlag>` can render as
 * `variant="legacy"`, i.e. the one where `color` and `level` are guaranteed to be
 * present. Mirrors that component's own conditions, so a flag is only kept when it
 * will actually produce output.
 */
const isLegacyPowerFlag = (
  flag: EfficiencyFlagsPowerFlag,
): flag is EfficiencyFlagsPowerFlag & {
  color: PowerEfficiencyFlag['color'];
  level: PowerEfficiencyFlag['level'];
} => !!flag.hasOldLabel && !!flag.level && !!flag.color && isValidColor(flag.color);

/**
 * Whether a flag can be rendered at all.
 *
 * A flag marked `hasOldLabel` is only kept when it is renderable *as a legacy label*.
 * It must never fall through to the current A-G scale: a legacy level can also be a
 * valid current class ("A"), so the fallback would show a green "A" for a product that
 * carries an old label — exactly the mismatch this component is meant to fix. If the
 * legacy label cannot be drawn (no usable colour), showing nothing is the safe outcome.
 */
const isRenderablePowerFlag = (flag: EfficiencyFlagsPowerFlag) =>
  flag.hasOldLabel ? isLegacyPowerFlag(flag) : isEfficiencyClass(flag.level);

/**
 * Single tire efficiency flag entry — plain props matching the PascalCase enum
 * used by both `BasketProduct.tireEfficiencyFlags` (`CheckoutTireEfficiencyFlag`)
 * and `WishlistLineItem.tireEfficiency` (`WishlistTireEfficiencyLabel`).
 */
export type EfficiencyFlagsTireFlag = {
  type: CheckoutTireEfficiencyFlag['type'];
  /** Displayed text (e.g. "B" or "B | 71 dB" for rolling noise). */
  level: string;
  /** Image URL shown in the overlay dialog. */
  link: string;
};

export type EfficiencyFlagsProps = {
  powerFlags?: readonly EfficiencyFlagsPowerFlag[] | null;
  tireFlags?: readonly EfficiencyFlagsTireFlag[] | null;
  /**
   * Optional product data sheet URL rendered once below the tire flag row
   * (consistent with how the previous `<PowerEfficiency>` component rendered it for tires).
   */
  tireDataSheet?: string | null;
};

/**
 * Composite renderer for power and tire efficiency flags used across basket, wishlist
 * and AddToBasketLayer line items. Built on the shared UI primitives
 * `<EnergyEfficiencyFlag>` and `<TireEfficiencyFlag>` so all three line-item surfaces
 * use the same visuals as the rest of the shop (PDP, ProductCardV2).
 */
export const EfficiencyFlags = ({ powerFlags, tireFlags, tireDataSheet }: EfficiencyFlagsProps) => {
  const renderablePowerFlags = powerFlags?.filter(isRenderablePowerFlag) ?? [];
  const hasPower = renderablePowerFlags.length > 0;
  const hasTire = !!tireFlags && tireFlags.length > 0;

  if (!hasPower && !hasTire) return null;

  return (
    <div className="flex flex-col items-start gap-1">
      {hasPower && !hasTire && (
        <div className="flex flex-col items-start gap-1">
          {renderablePowerFlags.map((flag, idx) => {
            const efficiencyProps: EnergieEfficiency | EnergieEfficiencyOld = isLegacyPowerFlag(
              flag,
            )
              ? { hasOldLabel: true, color: flag.color, level: flag.level }
              : { hasOldLabel: false, level: flag.level as EfficiencyClass };

            return (
              // eslint-disable-next-line @eslint-react/no-array-index-key
              <div className="flex items-center gap-1" key={flag.level ?? idx}>
                <EnergyEfficiencyFlagItem
                  {...efficiencyProps}
                  linkToEnergieEfficiencyLabel={flag.link}
                  overlayTitle={flag.text ?? undefined}
                  dataSheetUrl={flag.dataSheet || undefined}
                />
              </div>
            );
          })}
        </div>
      )}
      {hasTire && (
        <div className="flex flex-row flex-wrap items-center gap-1">
          {tireFlags.map((flag) => (
            <TireEfficiencyFlagItem
              key={`${flag.type}-${flag.level}`}
              text={flag.level}
              type={tireOrderToUiTypeMap[flag.type]}
              linkToEnergieEfficiencyLabel={flag.link}
              size="normal"
            />
          ))}
        </div>
      )}
      {hasTire && tireDataSheet ? (
        <DownloadItem link={tireDataSheet} type="PRODUCT_DATASHEET" renderIcon={false} />
      ) : null}
    </div>
  );
};
